import React, { useEffect, useState } from 'react';
import { useReactTable, flexRender } from '@tanstack/react-table';
import { getCoreRowModel } from '@tanstack/table-core';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

const WeeklyOrders = () => {
  const [orderData, setOrderData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDays, setSelectedDays] = useState([]);
  const [disabledDays, setDisabledDays] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);

    try {
      const response = await fetch(SummaryApi.allOrder.url, {
        method: SummaryApi.allOrder.method,
        headers: {
          Authorization: SummaryApi.bearerToken,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch orders');

      const data = await response.json();
      setOrderData((data.data || []).reverse());
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    const today = new Date().getDay();
    setSelectedDays(Array.from({ length: today + 1 }, (_, i) => i));
    setDisabledDays(Array.from({ length: 7 }, (_, i) => i > today));
  }, []);

  const filterOrders = () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay() + 1); // Monday
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    end.setHours(23, 59, 59, 999);

    return orderData.filter((order) => {
      if (!order.order_date) return false;

      const d = new Date(order.order_date);
      const day = d.getDay();

      return (
        d >= start &&
        d <= end &&
        selectedDays.includes(day) &&
        (searchTerm === '' ||
          order.order_code?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    });
  };

  const filteredOrders = filterOrders();

   const handleEdit = (order) => {
     navigate(`/admin-panel/orders/edit/${order.order_id}`, { state: { orderData: order } });
  };

  const columns = [
    { header: 'Order Code', accessorKey: 'order_code' },
    { header: 'Customer', accessorKey: 'customer_name' },
    { header: 'Mobile', accessorKey: 'mobile_number' },
    {
      header: 'Order Amount',
      accessorKey: 'order_amount',
      cell: ({ getValue }) => `₹ ${getValue()}`,
    },
    {
      header: 'Total Amount',
      accessorKey: 'total_amount',
      cell: ({ getValue }) => `₹ ${getValue()}`,
    },
    {
      header: 'Status',
      accessorKey: 'order_status',
      cell: ({ getValue }) => (
        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
          {getValue()}
        </span>
      ),
    },
    {
      header: 'Address',
      accessorKey: 'order_address',
      cell: ({ getValue }) => (
        <span className="block truncate max-w-[200px]">{getValue()}</span>
      ),
    },
    {
      header: 'Action',
      cell: ({ row }) => (
        <button
          onClick={() => handleEdit(row.original.order_id)}
          className="px-3 py-1 bg-blue-500 hover:bg-blue-700 text-white rounded"
        >
          Edit
        </button>
      ),
    },
  ];

  const table = useReactTable({
    data: filteredOrders,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const downloadExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredOrders);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'WeeklyOrders');
    XLSX.writeFile(wb, 'WeeklyOrders.xlsx');
  };

  return (
    <div className="bg-white p-4 rounded shadow">
      <h2 className="text-xl font-semibold mb-4">Weekly Orders</h2>

      {/* Search */}
      <input
        type="text"
        placeholder="Search Order Code..."
        className="border px-3 py-2 rounded w-full mb-4"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />

      {/* Weekday Filter */}
      <h3 className="text-md font-semibold mb-2">Select Days</h3>
      <div className="flex flex-wrap mb-4">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
          <label key={i} className="mr-4 flex items-center text-sm">
            <input
              type="checkbox"
              checked={selectedDays.includes(i)}
              disabled={disabledDays[i]}
              onChange={() =>
                setSelectedDays((prev) =>
                  prev.includes(i)
                    ? prev.filter((x) => x !== i)
                    : [...prev, i]
                )
              }
              className="mr-2"
            />
            {d}
          </label>
        ))}
      </div>

      {/* Actions */}
      <button
        onClick={downloadExcel}
        className="mb-4 px-4 py-2 bg-green-600 text-white rounded"
      >
        Download Excel
      </button>

      {/* Table */}
      <div className="overflow-x-auto border rounded">
        <table className="min-w-full">
          <thead className="bg-gray-100">
            {table.getHeaderGroups().map((hg) => (
              <tr key={hg.id}>
                {hg.headers.map((header) => (
                  <th key={header.id} className="text-left p-2">
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id} className="border-b">
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="p-2">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {loading && <p className="mt-4">Loading...</p>}
      {!loading && filteredOrders.length === 0 && (
        <p className="text-gray-500 mt-4">No orders found.</p>
      )}
    </div>
  );
};

export default WeeklyOrders;
